/**
 * Kreator — renderer wartości pola w trybie podglądu "na żywo" (makieta, bez sieci).
 *
 * Read-only, per-typ prezentacja wartości używana WSPÓLNIE przez render formularza
 * (`components/preview/PreviewFormSections.tsx`) i adapter listy
 * (`list-preview-adapter.tsx`). Celowo
 * lekki — bez `FieldRegistry` (który oczekuje `ModuleField` z backendu, ID-y, itd.)
 * i bez komponentów do edycji wartości (`ExtFieldValueInput`) — podgląd nie edytuje,
 * tylko pokazuje jak dane WYGLĄDAJĄ w formularzu/liście klienta.
 *
 * Referencje/relacje (`type === 'reference'`) renderują zwykły tekst syntetycznej
 * wartości wygenerowanej przez `generateSampleRecords` — bez żadnego zapytania do
 * API (moduł jeszcze nie istnieje u żadnego tenanta, nie ma czego resolvować).
 */

import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { ModuleTemplateFieldDefinition } from '@saas/shared';
import { ChevronDown } from 'lucide-react';

/** Pigułka (badge) w stylu accent — dla select / multi_select. */
function OptionPill({ label }: { label: string }): ReactNode {
  return (
    <span className="inline-flex items-center rounded-full bg-accent-100 px-2 py-0.5 text-xs font-medium text-accent-700 dark:bg-accent-900/40 dark:text-accent-300">
      {label}
    </span>
  );
}

function EmptyValue(): ReactNode {
  const { t } = useTranslation();
  return <span className="text-text-muted">{t('preview.emptyValue')}</span>;
}

function labelForOption(field: ModuleTemplateFieldDefinition, rawValue: string): string {
  const option = (field.options ?? []).find((o) => o.value === rawValue);
  return option?.label ?? rawValue;
}

/**
 * Renderuje wartość pola encji szablonu w trybie makiety, read-only.
 * Uniwersalny — używany zarówno w komórce listy jak i w polu formularza.
 */
export function renderPreviewFieldValue(
  field: ModuleTemplateFieldDefinition,
  value: unknown,
): ReactNode {
  if (value === null || value === undefined || value === '') {
    return <EmptyValue />;
  }

  switch (field.type) {
    case 'text':
      return <span className="text-text-primary">{String(value)}</span>;

    case 'longtext': {
      const text = String(value);
      const truncated = text.length > 120 ? `${text.slice(0, 120)}…` : text;
      return (
        <span className="text-text-primary" title={text}>
          {truncated}
        </span>
      );
    }

    case 'number':
      return <span className="text-text-primary">{String(value)}</span>;

    case 'decimal': {
      const num = typeof value === 'number' ? value : Number(value);
      const formatted = Number.isFinite(num)
        ? num.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : String(value);
      return <span className="font-mono text-text-primary">{formatted}</span>;
    }

    case 'date': {
      try {
        return (
          <span className="text-text-primary">
            {new Date(String(value)).toLocaleDateString('pl-PL')}
          </span>
        );
      } catch {
        return <span className="text-text-primary">{String(value)}</span>;
      }
    }

    case 'datetime': {
      try {
        return (
          <span className="text-text-primary">
            {new Date(String(value)).toLocaleString('pl-PL')}
          </span>
        );
      } catch {
        return <span className="text-text-primary">{String(value)}</span>;
      }
    }

    case 'boolean': {
      // Nie da się użyć useTranslation() tutaj (funkcja, nie komponent) — mały wrapper.
      return <BooleanValue value={Boolean(value)} />;
    }

    case 'select': {
      const raw = String(value);
      return <OptionPill label={labelForOption(field, raw)} />;
    }

    case 'multi_select': {
      const values = Array.isArray(value) ? (value as string[]) : [];
      if (values.length === 0) return <EmptyValue />;
      return (
        <span className="inline-flex flex-wrap gap-1">
          {values.map((v) => (
            <OptionPill key={v} label={labelForOption(field, v)} />
          ))}
        </span>
      );
    }

    case 'reference':
      // Syntetyczny string — brak id, brak sieci. Zwykły tekst (nie link/pill).
      return <span className="text-text-primary">{String(value)}</span>;

    default:
      return <span className="text-text-primary">{String(value)}</span>;
  }
}

function BooleanValue({ value }: { value: boolean }): ReactNode {
  const { t } = useTranslation();
  return (
    <span
      className={
        value
          ? 'inline-flex items-center rounded-full bg-success-100 px-2 py-0.5 text-xs font-medium text-success-700 dark:bg-success-900/40 dark:text-success-300'
          : 'inline-flex items-center rounded-full bg-bg-tertiary px-2 py-0.5 text-xs font-medium text-text-tertiary'
      }
    >
      {value ? t('preview.boolean.yes') : t('preview.boolean.no')}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Render pola jako INPUT (wygląd formularza klienta) — makieta, read-only.
//
// Podgląd FORMULARZA ma wyglądać jak formularz „Nowy rekord" u klienta: pola
// w obramowanych kontrolkach (input/select/textarea), nie jako goły tekst.
// Kontrolki nie są edytowalne (podgląd nie zapisuje) — odwzorowują tylko WYGLĄD.
// (Lista nadal używa `renderPreviewFieldValue` — kolorowe chipy statusów itd.)
// ---------------------------------------------------------------------------

/** Bazowa klasa obramowanej kontrolki (spójna z inputami panelu). */
const INPUT_BOX =
  'w-full rounded-lg border border-border-primary bg-bg-secondary px-3 py-2 text-sm text-text-primary';

/** Skalarny tekst wartości (data/kwota/liczba/tekst/referencja/opcja) do wyświetlenia w kontrolce. */
function scalarText(field: ModuleTemplateFieldDefinition, value: unknown): string {
  switch (field.type) {
    case 'decimal': {
      const num = typeof value === 'number' ? value : Number(value);
      return Number.isFinite(num)
        ? num.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : String(value);
    }
    case 'date':
      try {
        return new Date(String(value)).toLocaleDateString('pl-PL');
      } catch {
        return String(value);
      }
    case 'datetime':
      try {
        return new Date(String(value)).toLocaleString('pl-PL');
      } catch {
        return String(value);
      }
    case 'select':
      return labelForOption(field, String(value));
    default:
      return String(value);
  }
}

/**
 * Renderuje pole jako obramowaną kontrolkę formularza (input-like), read-only.
 * Select/multi_select → box z chevronem (jak dropdown), longtext → wyższy box
 * (jak textarea), boolean → checkbox + etykieta, reszta → box z wartością.
 */
export function renderPreviewFieldInput(
  field: ModuleTemplateFieldDefinition,
  value: unknown,
): ReactNode {
  const empty =
    value === null ||
    value === undefined ||
    value === '' ||
    (Array.isArray(value) && value.length === 0);

  if (field.type === 'boolean') {
    return <BooleanInput value={Boolean(value)} />;
  }

  if (field.type === 'select') {
    return (
      <div className={`${INPUT_BOX} flex items-center justify-between gap-2`}>
        {empty ? <EmptyValue /> : <span>{labelForOption(field, String(value))}</span>}
        <ChevronDown className="h-4 w-4 flex-shrink-0 text-text-muted" aria-hidden="true" />
      </div>
    );
  }

  if (field.type === 'multi_select') {
    const values = Array.isArray(value) ? (value as string[]) : [];
    return (
      <div className={`${INPUT_BOX} flex items-center justify-between gap-2`}>
        {values.length === 0 ? (
          <EmptyValue />
        ) : (
          <span className="flex flex-wrap gap-1">
            {values.map((v) => (
              <OptionPill key={v} label={labelForOption(field, v)} />
            ))}
          </span>
        )}
        <ChevronDown className="h-4 w-4 flex-shrink-0 text-text-muted" aria-hidden="true" />
      </div>
    );
  }

  if (field.type === 'longtext') {
    return (
      <div className={`${INPUT_BOX} min-h-[4.5rem] whitespace-pre-wrap`}>
        {empty ? <EmptyValue /> : String(value)}
      </div>
    );
  }

  // text / number / decimal / date / datetime / reference
  return <div className={INPUT_BOX}>{empty ? <EmptyValue /> : scalarText(field, value)}</div>;
}

/** Boolean jako checkbox (read-only) + etykieta Tak/Nie. */
function BooleanInput({ value }: { value: boolean }): ReactNode {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-2 text-sm text-text-primary">
      <span
        className={`flex h-4 w-4 items-center justify-center rounded border text-[10px] ${
          value
            ? 'border-accent-500 bg-accent-500 text-white'
            : 'border-border-primary bg-bg-secondary'
        }`}
        aria-hidden="true"
      >
        {value ? '✓' : ''}
      </span>
      <span>{value ? t('preview.boolean.yes') : t('preview.boolean.no')}</span>
    </div>
  );
}
